import React, { useState, useEffect } from 'react'
import { TrendingUp, Users, ShoppingCart, BarChart3, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import LoadingButton from '../components/LoadingButton'

interface AnalyticsPayload {
  overview: {
    totalRevenue: number
    totalOrders: number
    totalCustomers: number
    averageOrderValue: number
  }
  orderStats: {
    counts: Record<string, number>
    percentages: Record<string, number>
  }
  paymentStats: {
    counts: Record<string, number>
    percentages: Record<string, number>
  }
  performance: {
    conversionRate: number
    paidOrdersCount: number
    failedPaymentsCount: number
  }
}

export default function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshState, setRefreshState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  useEffect(() => {
    fetchAnalytics()
  }, [])

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      setRefreshState('loading')
      const res = await api.getAnalyticsData()
      const analyticsData = res.data?.data || res.data
      setData(analyticsData)
      setRefreshState('success')
      setTimeout(() => setRefreshState('idle'), 1000)
    } catch (error) {
      console.error('Analytics fetch error:', error)
      toast.error('Failed to load analytics data')
      setRefreshState('error')
      setTimeout(() => setRefreshState('idle'), 2000)
    } finally {
      setLoading(false)
    }
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D3A0D]"></div>
      </div>
    )
  }

  const { overview, orderStats, paymentStats, performance } = data || {
    overview: { totalRevenue: 0, totalOrders: 0, totalCustomers: 0, averageOrderValue: 0 },
    orderStats: { counts: {}, percentages: {} },
    paymentStats: { counts: {}, percentages: {} },
    performance: { conversionRate: 0, paidOrdersCount: 0, failedPaymentsCount: 0 },
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#6D3A0D]">Analytics Dashboard</h1>
          <p className="text-xs text-gray-500 mt-1">Real-time metrics pre-calculated from backend database</p>
        </div>
        <LoadingButton
          onClick={fetchAnalytics}
          state={refreshState}
          variant="secondary"
          size="sm"
          className="px-4 py-2 border-2 border-[#F6EEE7] text-[#6D3A0D] rounded-xl font-bold hover:bg-[#F6EEE7] transition flex items-center gap-2 text-xs"
        >
          <RefreshCw size={14} /> Refresh Data
        </LoadingButton>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Revenue */}
        <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 shadow-xs">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Revenue</h3>
            <div className="p-2 bg-green-50 text-green-600 rounded-xl">
              <span className='w-10 h-10'>₹</span>

            </div>
          </div>
          <p className="text-3xl font-bold text-[#6D3A0D]">₹{overview.totalRevenue.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-2 font-medium">All-time paid revenue</p>
        </div>

        {/* Total Orders */}
        <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 shadow-xs">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Orders</h3>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <ShoppingCart size={20} />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#6D3A0D]">{overview.totalOrders}</p>
          <p className="text-xs text-gray-500 mt-2 font-medium">Total customer orders</p>
        </div>

        {/* Total Customers */}
        <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 shadow-xs">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Customers</h3>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
              <Users size={20} />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#6D3A0D]">{overview.totalCustomers}</p>
          <p className="text-xs text-gray-500 mt-2 font-medium">Registered customer accounts</p>
        </div>

        {/* Average Order Value */}
        <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 shadow-xs">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Avg Order Value</h3>
            <div className="p-2 bg-orange-50 text-orange-600 rounded-xl">
              <TrendingUp size={20} />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#6D3A0D]">₹{overview.averageOrderValue.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-2 font-medium">Revenue per order</p>
        </div>
      </div>

      {/* Order Status Breakdown */}
      <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 shadow-xs">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#F6EEE7] text-[#6D3A0D] rounded-xl">
              <BarChart3 size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Order Status Breakdown</h2>
              <p className="text-xs text-gray-500">Distribution and exact percentage of order statuses</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { key: 'PENDING', label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-800', bar: 'bg-amber-500' },
            { key: 'CONFIRMED', label: 'Confirmed', bg: 'bg-blue-50', text: 'text-blue-800', bar: 'bg-blue-500' },
            { key: 'PROCESSING', label: 'Processing', bg: 'bg-indigo-50', text: 'text-indigo-800', bar: 'bg-indigo-500' },
            { key: 'SHIPPED', label: 'Shipped', bg: 'bg-purple-50', text: 'text-purple-800', bar: 'bg-purple-500' },
            { key: 'DELIVERED', label: 'Delivered', bg: 'bg-green-50', text: 'text-green-800', bar: 'bg-green-500' },
            { key: 'CANCELLED', label: 'Cancelled', bg: 'bg-red-50', text: 'text-red-800', bar: 'bg-red-500' },
          ].map((item) => {
            const count = orderStats.counts[item.key] || 0
            const percentage = orderStats.percentages[item.key] || 0

            return (
              <div key={item.key} className={`${item.bg} border border-black/5 rounded-xl p-4 space-y-2`}>
                <div className="flex justify-between items-center">
                  <span className={`text-xs font-bold uppercase ${item.text}`}>{item.label}</span>
                  <span className="text-xs font-bold text-gray-700">{percentage}%</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <div className="w-full bg-gray-200/80 rounded-full h-2 overflow-hidden">
                  <div
                    className={`${item.bar} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                  ></div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Payment Status Breakdown */}
      <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 shadow-xs">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-[#F6EEE7] text-[#6D3A0D] rounded-xl">
              <BarChart3 size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Payment Status Breakdown</h2>
              <p className="text-xs text-gray-500">Distribution and exact percentage of payment records</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'PENDING', label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-800', bar: 'bg-amber-500' },
            { key: 'PAID', label: 'Paid', bg: 'bg-green-50', text: 'text-green-800', bar: 'bg-green-500' },
            { key: 'FAILED', label: 'Failed', bg: 'bg-red-50', text: 'text-red-800', bar: 'bg-red-500' },
            { key: 'REFUNDED', label: 'Refunded', bg: 'bg-gray-100', text: 'text-gray-800', bar: 'bg-gray-500' },
          ].map((item) => {
            const count = paymentStats.counts[item.key] || 0
            const percentage = paymentStats.percentages[item.key] || 0

            return (
              <div key={item.key} className={`${item.bg} border border-black/5 rounded-xl p-4 space-y-2`}>
                <div className="flex justify-between items-center">
                  <span className={`text-xs font-bold uppercase ${item.text}`}>{item.label}</span>
                  <span className="text-xs font-bold text-gray-700">{percentage}%</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <div className="w-full bg-gray-200/80 rounded-full h-2 overflow-hidden">
                  <div
                    className={`${item.bar} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                  ></div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Performance Summary */}
      <div className="bg-gradient-to-r from-[#6D3A0D] to-[#8B4513] text-white rounded-2xl p-8 shadow-md">
        <h2 className="text-2xl font-bold mb-4">Performance Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/10 backdrop-blur-xs p-4 rounded-xl border border-white/10">
            <p className="text-amber-200 text-xs font-bold uppercase tracking-wider mb-1">Conversion Rate</p>
            <p className="text-3xl font-bold">{performance.conversionRate}%</p>
            <p className="text-[11px] text-amber-100/70 mt-1">(Total Orders ÷ Total Customers) × 100</p>
          </div>
          <div className="bg-white/10 backdrop-blur-xs p-4 rounded-xl border border-white/10">
            <p className="text-amber-200 text-xs font-bold uppercase tracking-wider mb-1">Successful Paid Orders</p>
            <p className="text-3xl font-bold">{performance.paidOrdersCount}</p>
            <p className="text-[11px] text-amber-100/70 mt-1">Confirmed paid transactions</p>
          </div>
          <div className="bg-white/10 backdrop-blur-xs p-4 rounded-xl border border-white/10">
            <p className="text-amber-200 text-xs font-bold uppercase tracking-wider mb-1">Failed Payments</p>
            <p className="text-3xl font-bold">{performance.failedPaymentsCount}</p>
            <p className="text-[11px] text-amber-100/70 mt-1">Transactions requiring follow-up</p>
          </div>
        </div>
      </div>
    </div>
  )
}
